import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./EnvironmentVariables";

export const msalInstance = new PublicClientApplication(msalConfig);

export async function initializeMsalInstance(): Promise<void> {
  await msalInstance.initialize();

  // Set the active account whenever a login or token acquisition succeeds.
  // Without this, getActiveAccount() returns null after a redirect login,
  // and callers fall back to accounts[0] which may not be the user who just
  // signed in (e.g. multi-account browsers).
  msalInstance.addEventCallback((event) => {
    const isLoginOrAcquireSuccess =
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS;

    if (isLoginOrAcquireSuccess && event.payload && 'account' in event.payload) {
      const account = (event.payload as { account?: { homeAccountId: string } | null }).account;
      if (account) {
        msalInstance.setActiveAccount(account as Parameters<typeof msalInstance.setActiveAccount>[0]);
      }
    }
  });

  // In MSAL v5, navigateToLoginRequestUrl moved from config to handleRedirectPromise options.
  // Call it here before MsalProvider mounts so the cached result is reused internally,
  // preventing MSAL from navigating back to the pre-login page (e.g. /login).
  // Capture the result and set the active account from it — this covers the case
  // where the redirect completes BEFORE the event callback above is registered.
  try {
    const result = await msalInstance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    }
  } catch (e: any) {
    if (e?.errorCode === 'no_token_request_cache_error') {
      console.warn('[MSAL] Redirect state lost, restarting login');
    } else {
      throw e;
    }
  }
}
